import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { registerPwaLifecycle, type PwaState } from './pwa-lifecycle';

type PwaContextValue = {
  state: PwaState;
  install(): Promise<void>;
  applyUpdate(): Promise<void>;
};

const initialState: PwaState = {
  supported: true,
  offline: !navigator.onLine,
  updateReady: false,
  installReady: false,
};

const PwaContext = createContext<PwaContextValue | null>(null);

export function PwaProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(initialState);
  const lifecycle = useRef<ReturnType<typeof registerPwaLifecycle> | null>(null);

  useEffect(() => {
    lifecycle.current = registerPwaLifecycle(setState);
    return () => lifecycle.current?.dispose();
  }, []);

  const value = useMemo<PwaContextValue>(
    () => ({
      state,
      install: async () => lifecycle.current?.install(),
      applyUpdate: async () => {
        await lifecycle.current?.applyUpdate();
      },
    }),
    [state],
  );

  return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>;
}

// Provider and hook intentionally share this small lifecycle boundary.
// eslint-disable-next-line react-refresh/only-export-components
export function usePwa(): PwaContextValue {
  const value = useContext(PwaContext);
  if (!value) throw new Error('usePwa must be used within PwaProvider');
  return value;
}
